// src/data/categoryOptions.js

/**
 * ✅ Canonical category list (single source of truth)
 * - Use CATEGORY_OPTIONS everywhere (owner edit, admin edit, public filters)
 * - Use normalizeCategory() before saving to Firestore to prevent drift
 */

export const CATEGORY_OPTIONS = [
  "Restaurant",
  "Cafe",
  "Bakery",

  "Barber Shop",
  "Salon",
  "Beauty / Spa",

  "Auto Repair",
  "Tire Shop",
  "Car Wash",
  "Gas Station",

  "Doctor / Clinic",
  "Dentist",
  "Pharmacy",
  "Veterinary",
  "Pet Services",

  "Grocery",
  "Retail",
  "Hardware",

  "Gym / Fitness",
  "Entertainment",

  "Hotel",
  "Real Estate",
  "Legal Services",
  "Accounting",
  "Insurance",
  "Bank / Credit Union",

  "IT Services",
  "Education / Tutoring",
  "Photography",

  "Construction",
  "Electrician",
  "Plumbing",
  "Landscaping",
  "Cleaning Services",
  "Laundry",
  "Moving Services",
  "Courier / Delivery",

  "Other",
];

/** Fast lookup set */
const CATEGORY_SET = new Set(CATEGORY_OPTIONS.map((x) => String(x).trim().toLowerCase()));

/**
 * Optional: legacy aliases (helps auto-fix old/free-typed values)
 * Add more as you find old variants in Firestore.
 */
const CATEGORY_ALIASES = new Map(
  Object.entries({
    "mechanic": "Auto Repair",
    "auto repair shop": "Auto Repair",
    "auto mechanic": "Auto Repair",
    "tire": "Tire Shop",
    "tires": "Tire Shop",
    "carwash": "Car Wash",
    "car wash": "Car Wash",

    "doctor": "Doctor / Clinic",
    "clinic": "Doctor / Clinic",
    "medical clinic": "Doctor / Clinic",

    "spa": "Beauty / Spa",
    "beauty": "Beauty / Spa",

    "food": "Restaurant",
    "restaurant / food": "Restaurant",

    "delivery": "Courier / Delivery",
    "courier": "Courier / Delivery",
  })
);

/**
 * ✅ Normalize any incoming category value to the canonical value
 * - If it's already valid, returns the canonical option
 * - If it's a known alias, returns the mapped option
 * - Otherwise returns "" (meaning: treat as unset) unless allowOther=true
 */
export function normalizeCategory(input, { allowOther = true } = {}) {
  const raw = String(input ?? "").trim();
  if (!raw) return "";

  const key = raw.toLowerCase();

  // direct match
  if (CATEGORY_SET.has(key)) {
    // return the canonical casing
    const canonical = CATEGORY_OPTIONS.find((c) => c.toLowerCase() === key);
    return canonical || raw;
  }

  // alias match
  const aliasHit = CATEGORY_ALIASES.get(key);
  if (aliasHit) return aliasHit;

  // unknown -> optionally collapse to Other
  return allowOther ? "Other" : "";
}

/**
 * ✅ Convenience: validate if category is an allowed canonical option
 */
export function isValidCategory(input) {
  const key = String(input ?? "").trim().toLowerCase();
  return CATEGORY_SET.has(key);
}
